import { useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema } from '@solar/shared';
import { LogIn } from 'lucide-react';
import { Brand } from '@/components/layout/Brand';
import { useAuth } from '@/auth/AuthContext';
import { errorMessage } from '@/lib/api';
import { isSubAppPath } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/field';
import { Alert, Spinner } from '@/components/ui/feedback';

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const form = useForm({ resolver: zodResolver(loginSchema), defaultValues: { login: '', password: '' } });
  const next = params.get('next');
  const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';

  if (loading) return <Spinner className="min-h-screen" />;
  if (user) {
    if (!user.mustChangePassword && isSubAppPath(safeNext)) {
      window.location.replace(safeNext);
      return <Spinner className="min-h-screen" />;
    }
    return <Navigate to={user.mustChangePassword ? '/doi-mat-khau' : safeNext} replace />;
  }

  const submit = form.handleSubmit(async (v) => {
    setError(null);
    try {
      await login(v.login, v.password);
      // Module ứng dụng riêng (sub-app) không thuộc router của SPA này → tải trang đầy đủ
      if (isSubAppPath(safeNext)) window.location.assign(safeNext);
      else navigate(safeNext, { replace: true });
    } catch (e) {
      setError(errorMessage(e));
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-page px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center">
          <Brand size="lg" />
          <h1 className="mt-5 text-xl font-bold tracking-[-0.4px] text-ink">Nền tảng quản trị doanh nghiệp</h1>
          <p className="mt-1 text-sm text-[#87909d]">Dành cho nhân viên doanh nghiệp</p>
        </div>
        <form onSubmit={submit} className="space-y-4 rounded-xl border border-line bg-white p-6 shadow-sm" noValidate>
          {error && <Alert tone="error">{error}</Alert>}
          <Field
            label="Tên đăng nhập hoặc email"
            htmlFor="login"
            error={form.formState.errors.login?.message}
          >
            <Input
              id="login"
              autoComplete="username"
              autoFocus
              {...form.register('login')}
              aria-invalid={!!form.formState.errors.login}
            />
          </Field>
          <Field label="Mật khẩu" htmlFor="password" error={form.formState.errors.password?.message}>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              {...form.register('password')}
              aria-invalid={!!form.formState.errors.password}
            />
          </Field>
          <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
            <LogIn /> Đăng nhập
          </Button>
          <p className="text-center text-xs text-slate-500">
            Tài khoản do quản trị viên cấp. Quên mật khẩu? Liên hệ quản trị viên để đặt lại.
          </p>
        </form>
      </div>
    </div>
  );
}
